import type { AuthenticationEnvironment } from "./session";

export type PublicAuthenticationConfiguration =
  | Readonly<{ state: "disconnected"; environment: "local" }>
  | Readonly<{
      state: "connected";
      environment: "shared-test" | "future-live";
      workOsClientId: string;
      convexUrl: string;
    }>;

export type AdministratorAuthenticationConfiguration =
  | Readonly<{ state: "disconnected"; environment: "local" }>
  | Readonly<{
      state: "connected";
      environment: "shared-test" | "future-live";
      workOsClientId: string;
      convexUrl: string;
      administratorOrganizationId: string;
    }>;

export type ConsumerWebAuthenticationConfiguration =
  | Readonly<{ state: "disconnected"; environment: "local" }>
  | Readonly<{
      state: "connected";
      environment: "shared-test" | "future-live";
      convexUrl: string;
    }>;

type PublicAuthenticationConfigurationInput = Readonly<{
  environment: string | undefined;
  workOsClientId: string | undefined;
  convexUrl: string | undefined;
}>;

const configurationInvalidMessage = "Public authentication configuration is incomplete or invalid.";
const workOsClientIdPattern = /^client_[A-Za-z0-9]+$/u;
const workOsOrganizationIdPattern = /^org_[A-Za-z0-9]+$/u;

function parseEnvironment(value: string | undefined): AuthenticationEnvironment {
  if (value === "local" || value === "shared-test" || value === "future-live") {
    return value;
  }
  throw new Error(configurationInvalidMessage);
}

function isApprovedConvexUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.hostname.endsWith(".convex.cloud") &&
      url.hostname.length > ".convex.cloud".length &&
      url.username === "" &&
      url.password === "" &&
      url.port === "" &&
      url.pathname === "/" &&
      url.search === "" &&
      url.hash === ""
    );
  } catch {
    return false;
  }
}

export function parsePublicAuthenticationConfiguration(
  input: PublicAuthenticationConfigurationInput,
): PublicAuthenticationConfiguration {
  const environment = parseEnvironment(input.environment);
  if (environment === "local") {
    if (input.workOsClientId !== undefined || input.convexUrl !== undefined) {
      throw new Error(configurationInvalidMessage);
    }
    return { state: "disconnected", environment };
  }

  if (
    input.workOsClientId === undefined ||
    !workOsClientIdPattern.test(input.workOsClientId) ||
    input.convexUrl === undefined ||
    !isApprovedConvexUrl(input.convexUrl)
  ) {
    throw new Error(configurationInvalidMessage);
  }

  return {
    state: "connected",
    environment,
    workOsClientId: input.workOsClientId,
    convexUrl: input.convexUrl,
  };
}

export function parseConsumerWebAuthenticationConfiguration(input: {
  readonly environment: string | undefined;
  readonly convexUrl: string | undefined;
}): ConsumerWebAuthenticationConfiguration {
  const environment = parseEnvironment(input.environment);
  if (environment === "local") {
    if (input.convexUrl !== undefined) throw new Error(configurationInvalidMessage);
    return { state: "disconnected", environment };
  }
  if (input.convexUrl === undefined || !isApprovedConvexUrl(input.convexUrl)) {
    throw new Error(configurationInvalidMessage);
  }
  return { state: "connected", environment, convexUrl: input.convexUrl };
}

export function parseAdministratorAuthenticationConfiguration(
  input: PublicAuthenticationConfigurationInput &
    Readonly<{ administratorOrganizationId: string | undefined }>,
): AdministratorAuthenticationConfiguration {
  const publicConfiguration = parsePublicAuthenticationConfiguration(input);
  if (publicConfiguration.state === "disconnected") {
    if (input.administratorOrganizationId !== undefined) {
      throw new Error(configurationInvalidMessage);
    }
    return publicConfiguration;
  }
  if (
    input.administratorOrganizationId === undefined ||
    !workOsOrganizationIdPattern.test(input.administratorOrganizationId)
  ) {
    throw new Error(configurationInvalidMessage);
  }
  return {
    ...publicConfiguration,
    administratorOrganizationId: input.administratorOrganizationId,
  };
}
